import React, { useContext, useMemo, useState } from "react";
import getTalentExportString from "../../lib/wowTalentExporter";
import { ReactComponent as LinkIcon } from "svg/link.svg";
import { ReactComponent as CheckIcon } from "svg/check-fill.svg";
import { getParsedSelectedTalents } from "../../lib/wowHelpers";
import { WoWContext } from "../App";

const getMaxValue = (node, specType, selectedTalentNodeIds) => {
  if (node.spells.length > 0) {
    if (node.spells.length > 1) {
      node.spells.forEach((spell) => {
        // Use nodeEntryId for data from WCL and the definition ID for data from the Blizzard API.
        if (
          selectedTalentNodeIds.includes(spell.nodeEntryId.toString()) ||
          selectedTalentNodeIds.includes(spell.definitionId.toString())
        ) {
          return spell.maxRanks;
        }
      });
    }
    return node.spells[0].maxRanks;
  }
};

const buildNode = (
  nodeConfig,
  key,
  node,
  selectedTalents,
  selectedTalentNodeIds,
  selectedTalentsValues,
  specType,
  isDynamic,
  isMaxedOut,
  isWrongSpec,
) => {
  nodeConfig[key] = {};
  // If this node is in the list of selected talents
  // If isWrongSpec is true - then this is a tree for the wrong spec and all nodes should be not selected
  if (!isMaxedOut && !isWrongSpec && selectedTalents.includes(key.toString())) {
    nodeConfig[key].isSelected = true;

    // If this is a choice node (type 3)
    if (node.type === 3 || node.type === "choice") {
      nodeConfig[key].isChoiceNode = true;

      // Find the spell that is selected - this is the choice node entry
      node.spells.forEach((spell, index) => {
        // Use nodeEntryId for data from WCL and the definition ID for data from the Blizzard API.
        if (
          selectedTalentNodeIds.includes(spell.nodeEntryId.toString()) ||
          selectedTalentNodeIds.includes(spell.definitionId.toString())
        ) {
          nodeConfig[key].choiceEntrySelected = index;
        }
      });
    } else {
      nodeConfig[key].isChoiceNode = false;
    }

    // Get the number of points that are invested in this node
    const nodeID = isDynamic ? node.originalNodeId : node.id;
    const actualValue = parseInt(selectedTalentsValues[selectedTalents.indexOf(nodeID.toString())]);
    // If the number of points does not match the maximum possible value, it is partially filled
    if (getMaxValue(node, specType, selectedTalentNodeIds) !== actualValue) {
      nodeConfig[key].isPartiallyFilled = true;
      nodeConfig[key].pointsInvested = actualValue;
    } else {
      nodeConfig[key].isPartiallyFilled = false;
    }
  } else {
    nodeConfig[key].isSelected = false;
  }

  return nodeConfig[key];
};

const buildApexNodeConfig = (nodeConfig, apexNode, talentsData, specType, isWrongSpec) => {
  if (!apexNode || !apexNode.id) return nodeConfig;

  const { selectedTalents, selectedTalentNodeIds, selectedTalentsValues } = getParsedSelectedTalents(talentsData);
  const key = apexNode.id.toString();
  const isMaxedOut = apexNode.alreadyMaxedOut;

  nodeConfig[key] = buildNode(
    nodeConfig,
    key,
    apexNode,
    selectedTalents,
    selectedTalentNodeIds,
    selectedTalentsValues,
    specType,
    false,
    isMaxedOut,
    isWrongSpec,
  );

  return nodeConfig;
};

const buildNodeConfig = (nodeConfig, tree, talentsData, specType, isWrongSpec) => {
  const { selectedTalents, selectedTalentNodeIds, selectedTalentsValues } = getParsedSelectedTalents(talentsData);

  // Iterate over every node in the tree
  Object.entries(tree).map(([key, node], i) => {
    const isMaxedOut = node.alreadyMaxedOut;
    nodeConfig[key] = buildNode(
      nodeConfig,
      key,
      node,
      selectedTalents,
      selectedTalentNodeIds,
      selectedTalentsValues,
      specType,
      false,
      isMaxedOut,
      isWrongSpec,
    );
  });

  return nodeConfig;
};

const CopyTalentsButton = (props) => {
  const { label, className = "" } = props;
  const talentsContext = useContext(WoWContext);
  const [showPopup, setShowPopup] = useState(false);
  const specId = talentsContext.allTrees[talentsContext.specType]?.id ?? 0;
  const heroMetaNodeID = talentsContext.allTrees[talentsContext.specType]?.hero?.metaNodeId ?? 0;
  const sanitizedSpecType = talentsContext.specType.replace(/_/g, "-");

  let nodeConfig = {};

  const trees = useMemo(() => [...Object.values(talentsContext.allTrees)].filter((x) => !!x), [talentsContext.allTrees]);
  trees.forEach((tree) => {
    // Skip the trees for this spec - save it for last so nodes aren't overwritten
    if (tree.id !== specId) {
      nodeConfig = buildNodeConfig(nodeConfig, tree?.specNodes, talentsContext.selectedTalents, sanitizedSpecType, true);
      nodeConfig = buildNodeConfig(nodeConfig, tree?.classNodes, talentsContext.selectedTalents, sanitizedSpecType, true);

      nodeConfig = buildNodeConfig(
        nodeConfig,
        tree?.hero.left.nodes ?? {},
        talentsContext.selectedTalents,
        sanitizedSpecType,
        true,
      );

      nodeConfig = buildNodeConfig(
        nodeConfig,
        tree?.hero.right.nodes ?? {},
        talentsContext.selectedTalents,
        sanitizedSpecType,
        true,
      );

      nodeConfig = buildApexNodeConfig(nodeConfig, tree?.apexNode, talentsContext.selectedTalents, sanitizedSpecType, true);

      nodeConfig[tree?.hero?.metaNodeId] = {};
      nodeConfig[tree?.hero?.metaNodeId].isSelected = false;
    }
  });

  nodeConfig = buildNodeConfig(
    nodeConfig,
    talentsContext.allTrees[talentsContext.specType]?.specNodes ?? {},
    talentsContext.selectedTalents,
    sanitizedSpecType,
    false,
  );

  nodeConfig = buildNodeConfig(
    nodeConfig,
    talentsContext.allTrees[talentsContext.specType]?.classNodes ?? {},
    talentsContext.selectedTalents,
    sanitizedSpecType,
    false,
  );

  nodeConfig = buildNodeConfig(
    nodeConfig,
    talentsContext.allTrees[talentsContext.specType]?.hero.left.nodes ?? {},
    talentsContext.selectedTalents,
    sanitizedSpecType,
    false,
  );

  nodeConfig = buildNodeConfig(
    nodeConfig,
    talentsContext.allTrees[talentsContext.specType]?.hero.right.nodes ?? {},
    talentsContext.selectedTalents,
    sanitizedSpecType,
    false,
  );

  nodeConfig = buildApexNodeConfig(
    nodeConfig,
    talentsContext.allTrees[talentsContext.specType]?.apexNode,
    talentsContext.selectedTalents,
    sanitizedSpecType,
    false,
  );

  // This adds the metaNodeId parameters that are not included in the talent string
  const metaNodeId = talentsContext.allTrees[talentsContext.specType]?.hero?.metaNodeId ?? [];
  if (metaNodeId != []) {
    nodeConfig[metaNodeId] = {};
    nodeConfig[metaNodeId].isSelected = true;
    nodeConfig[metaNodeId].isChoiceNode = true;
    nodeConfig[metaNodeId].choiceEntrySelected = talentsContext.selectedHero === "left" ? 0 : 1;
    nodeConfig[metaNodeId].isPartiallyFilled = false;
  }

  // Some trees have an attribute called "extraNodeIds"
  // These need to be accounted for as non-selected nodes
  const extraNodeIds = talentsContext.allTrees[talentsContext.specType]?.extraNodeIds ?? [];
  if (extraNodeIds && extraNodeIds.length > 0) {
    extraNodeIds.forEach((nodeId) => {
      nodeConfig[nodeId] = {};
      nodeConfig[nodeId].isSelected = false;
    });
  }

  const handleClick = () => {
    navigator.clipboard.writeText(getTalentExportString(specId, heroMetaNodeID, nodeConfig));
    setShowPopup(true);
    setTimeout(() => {
      setShowPopup(false);
    }, 2000);
  };

  return (
    <div className={`export-container ${className}`}>
      {showPopup && (
        <div className="popup-container">
          <div className="copied-popup">
            <CheckIcon className="check-icon" />
            Copied
          </div>
          <div className="popup-point" />
        </div>
      )}
      <button className="export-button" onClick={() => handleClick()}>
        <LinkIcon className="link-icon" />
        {label}
      </button>
    </div>
  );
};

export default CopyTalentsButton;
